import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTasks } from '../hooks/useTasks';
import { useSearch } from '../hooks/useSearch';
import { useDragSort } from '../hooks/useDragSort';
import TaskItem from '../components/TaskItem';
import SearchBar from '../components/SearchBar';
import { QuickAdd, TaskModal } from '../components/TaskModal';
import { ProgressRing, EmptyState, Spinner, OfflineBadge } from '../components/ui';

const FILTERS = [
  { key: 'Today', label: 'Today' },
  { key: 'All',   label: 'All'   },
  { key: 'Later', label: 'Later' },
];

function formatDate() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function PlusIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}


// Drag-and-drop visual feedback via CSS data-attributes (no React state during drag)
const DRAG_CSS = `
  [data-dragging="true"] { opacity: 0.4 !important; cursor: grabbing !important; }
  [data-dragover="true"]  { outline: 2px solid var(--accent) !important; outline-offset: -2px; }
`;
export default function TasksPage() {
  const { user, syncing, syncDone } = useAuth();
  const {
    tasks, categories, loading,
    addTask, toggleTask, deleteTask, updateTask, reorderTasks, isOnline, refetch,
  } = useTasks();

  const [filter,    setFilter]    = useState('Today');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTask,  setEditTask]  = useState(null);
  const [showDone,  setShowDone]  = useState(false);

  const { query, setQuery, results: searchResults, isSearching, isActive: searchActive, clear: clearSearch } = useSearch(tasks, isOnline);

  // After offline sync completes, reload tasks from server so offline IDs are replaced
  useEffect(() => {
    if (syncDone) refetch();
  }, [syncDone]);

  const handleSelectResult = (task) => { clearSearch(); setEditTask(task); setModalOpen(true); };
  const handleOpenEdit     = (task) => { setEditTask(task); setModalOpen(true); };
  const handleCloseModal   = ()     => { setModalOpen(false); setEditTask(null); };
  const handleSave         = async (data) => {
    if (editTask) await updateTask(editTask.id, data);
    else          await addTask(data);
  };

  const filtered = useMemo(() => {
    const f = tasks.filter(t => {
      if (filter === 'Today') return t.due === 'today';
      if (filter === 'Later') return t.due === 'later';
      return true;
    });
    return [...f].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [tasks, filter]);

  const pendingRaw = filtered.filter(t => !t.done);
  const completed  = filtered.filter(t =>  t.done);

  // Drag-to-reorder pending tasks
  const { items: pending, rowProps, handleProps } = useDragSort(pendingRaw, reorderTasks);

  const todayTasks = tasks.filter(t => t.due === 'today');
  const todayDone  = todayTasks.filter(t => t.done).length;

  const searchPending   = searchResults.filter(t => !t.done);
  const searchCompleted = searchResults.filter(t =>  t.done);

  if (loading) {
    return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}><Spinner size={28}/></div>;
  }

  return (
    <div style={{ maxWidth: 660, margin: '0 auto', padding: 'clamp(16px,4vw,32px) clamp(12px,4vw,24px) 100px' }}>
      <style>{DRAG_CSS}</style>

      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 2 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700, color:'var(--text)', letterSpacing:'-0.5px' }}>
                {searchActive
                  ? `Results for "${query}"`
                  : filter === 'Today' ? 'Today'
                  : filter === 'Later' ? 'Later'
                  : 'All tasks'}
              </h1>
              {!isOnline && <OfflineBadge />}
            </div>
            {filter === 'Today' && !searchActive && (
              <p style={{ fontSize: 13, color:'var(--text3)' }}>{formatDate()}</p>
            )}
          </div>
          {filter === 'Today' && !searchActive && todayTasks.length > 0 && (
            <ProgressRing done={todayDone} total={todayTasks.length} size={44} />
          )}
        </div>

        {!isOnline && (
          <div style={{ marginTop: 12, background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 10, padding:'12px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap: 12, flexWrap:'wrap' }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color:'var(--text)', marginBottom: 1 }}>You're in guest mode</p>
              <p style={{ fontSize: 12, color:'var(--text3)' }}>Tasks save locally. Sign in to sync across devices.</p>
            </div>
            <Link to="/login" style={{ textDecoration:'none', flexShrink: 0 }}>
              <button style={{ background:'var(--brand-grad)', border:'none', borderRadius: 8, color:'white', fontWeight: 700, fontSize: 12, padding:'7px 14px', cursor:'pointer', fontFamily:'inherit' }}>Sign in</button>
            </Link>
          </div>
        )}

        {syncing && (
          <div style={{ marginTop: 10, background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 10, padding:'10px 14px', display:'flex', alignItems:'center', gap: 10 }}>
            <Spinner size={15}/>
            <span style={{ fontSize: 13, color:'var(--text2)', fontWeight: 500 }}>Syncing offline tasks…</span>
          </div>
        )}
        {syncDone && (
          <div style={{ marginTop: 10, background:'rgba(62,224,122,0.08)', border:'1px solid rgba(62,224,122,0.25)', borderRadius: 10, padding:'10px 14px', fontSize: 13, color:'#16A34A', fontWeight: 600 }}>
            ✓ All offline tasks synced!
          </div>
        )}
      </div>

      {/* Tabs + Add */}
      <div style={{ display:'flex', alignItems:'center', marginBottom: 14, borderBottom:'1px solid var(--border)', flexWrap:'wrap', gap:'0 4px' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => { setFilter(f.key); clearSearch(); }} style={{
            padding:'8px 13px', fontSize: 13, fontWeight: 500, cursor:'pointer',
            fontFamily:'inherit', background:'none', border:'none',
            borderBottom: filter === f.key && !searchActive ? '2px solid var(--accent)' : '2px solid transparent',
            color: filter === f.key && !searchActive ? 'var(--accent)' : 'var(--text3)',
            marginBottom:'-1px', transition:'all 0.15s',
          }}>{f.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => { setEditTask(null); setModalOpen(true); }}
          style={{ display:'flex', alignItems:'center', gap: 5, padding:'6px 12px', borderRadius: 7, marginBottom: 6, background:'var(--brand-grad)', border:'none', color:'white', fontWeight: 600, fontSize: 12, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.filter='brightness(1.08)'}
          onMouseLeave={e => e.currentTarget.style.filter=''}
        >
          <PlusIcon /> Add task
        </button>
      </div>

      {/* Search */}
      <SearchBar
        query={query} onChange={setQuery} onClear={clearSearch}
        results={searchResults} isSearching={isSearching}
        isActive={searchActive} onSelectResult={handleSelectResult}
      />

      {/* Content */}
      {searchActive ? (
        <>
          {searchPending.length > 0 && (
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 10, overflow:'hidden', marginTop: 4 }}>
              {searchPending.map(t => <TaskItem key={t.id} task={t} onToggle={toggleTask} onDelete={deleteTask} onEdit={handleOpenEdit}/>)}
            </div>
          )}
          {searchCompleted.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontSize: 12, color:'var(--text3)', fontWeight: 500, marginBottom: 6 }}>Completed · {searchCompleted.length}</p>
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 10, overflow:'hidden', opacity: 0.65 }}>
                {searchCompleted.map(t => <TaskItem key={t.id} task={t} onToggle={toggleTask} onDelete={deleteTask} onEdit={handleOpenEdit}/>)}
              </div>
            </div>
          )}
          {!isSearching && searchResults.length === 0 && query.trim().length >= 2 && (
            <EmptyState icon="🔍" title={`No results for "${query}"`} sub="Try a different word, or browse your tasks below."/>
          )}
        </>
      ) : (
        <>
          <QuickAdd onAdd={addTask} categories={categories}/>

          {pending.length > 0 && (
            <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 10, marginTop: 8 }}>
              {pending.map(t => (
                <TaskItem
                  key={t.id}
                  task={t}
                  onToggle={toggleTask}
                  onDelete={deleteTask}
                  onEdit={handleOpenEdit}
                  rowProps={rowProps(t.id)}
                  handleProps={handleProps(t.id)}
                />
              ))}
            </div>
          )}

          {pending.length === 0 && completed.length === 0 && (
            <EmptyState
              icon="✓"
              title={filter === 'Today' ? "You're all caught up!" : 'No tasks here'}
              sub={filter === 'Today' ? 'Add something to tackle today.' : 'Add a task to get started.'}
            />
          )}

          {completed.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <button onClick={() => setShowDone(p => !p)} style={{ display:'flex', alignItems:'center', gap: 7, background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize: 13, fontWeight: 500, fontFamily:'inherit', padding:'4px 0', marginBottom: 8 }}>
                <span style={{ display:'inline-block', transform: showDone ? 'rotate(90deg)' : 'rotate(0)', transition:'transform 0.15s', fontSize: 10 }}>▶</span>
                Completed · {completed.length}
              </button>
              {showDone && (
                <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 10, overflow:'hidden', opacity: 0.65 }}>
                  {completed.map(t => <TaskItem key={t.id} task={t} onToggle={toggleTask} onDelete={deleteTask} onEdit={handleOpenEdit}/>)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <TaskModal open={modalOpen} onClose={handleCloseModal} onSave={handleSave} task={editTask} categories={categories}/>
    </div>
  );
}